"use client";

import { useState, useEffect, useContext } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import styles from "./SignIn.module.css";
import { FaUser, FaLock, FaExclamationCircle, FaEye, FaEyeSlash } from "react-icons/fa";
import pencil from "../assets/pencil.png";
import login1 from "../assets/login1.png";
import login2 from "../assets/login2.png";
import Footer from "../MyComponents/Footer";
import GuestNav from "./Navbars/GuestNav";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ToastContainer } from "react-toastify";
import axios from "axios";
import { UserContext } from "./UserContext";
import { FaTimes } from "react-icons/fa";

const SignIn = () => {
  const { setUserRole, setUserInfo, logout } = useContext(UserContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [isLogin, setIsLogin] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
  });
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});

  // Simplified login - only uses existing backend APIs (POST /login and GET /)

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const formType = searchParams.get("form");
    if (formType === "signup") {
      setIsLogin(false);
    }
  }, [location]);

  // Check for remembered username on component mount
  useEffect(() => {
    const rememberedUsername = localStorage.getItem('rememberedUsername');
    const isRemembered = localStorage.getItem('rememberMe') === 'true';

    if (rememberedUsername && isRemembered) {
      setFormData(prev => ({
        ...prev,
        username: rememberedUsername
      }));
      setRememberMe(true);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const validateForm = () => {
    const newErrors = {};

    // Username validation (no email check)
    if (!formData.username.trim()) {
      newErrors.username = "Username is required";
    } else if (formData.username.length < 3) {
      newErrors.username = "Username must be at least 3 characters";
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password.length > 50) {
      newErrors.password = "Password must be less than 50 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };



  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors in the form!", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 3000,
      });
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${(process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_LOCAL_SERVER_API)}/login`,
        {
          loginUsername: formData.username.trim(),
          loginPassword: formData.password,
        },
        {
          withCredentials: true
        }
      );

      if (response.data.Login) {
        setUserRole(response.data.usertype);

        // PRIORITY 1: Try to get user information from session (most reliable)
        await new Promise(resolve => setTimeout(resolve, 1500));

        try {
          const sessionResponse = await axios.get(
            `${(process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_LOCAL_SERVER_API)}/`,
            { withCredentials: true }
          );

          if (sessionResponse.data.valid && sessionResponse.data.User_id) {

            // Store complete user information from session
            const userData = {
              User_id: sessionResponse.data.User_id,
              username: sessionResponse.data.username || formData.username.trim(),
              usertype: sessionResponse.data.usertype || response.data.usertype,
              firstname: sessionResponse.data.firstname,
              lastname: sessionResponse.data.lastname,
              avatar: sessionResponse.data.avatar
            };

            setUserInfo(userData);
            localStorage.setItem('User_id', sessionResponse.data.User_id.toString());
            localStorage.setItem('usertype', userData.usertype);
            localStorage.setItem('username', userData.username);

            // Handle remember me functionality
            if (rememberMe) {
              localStorage.setItem('rememberMe', 'true');
              localStorage.setItem('rememberedUsername', formData.username.trim());
            } else {
              localStorage.removeItem('rememberMe');
              localStorage.removeItem('rememberedUsername');
            }

            setUserRole(userData.usertype);



            toast.success("Login successful!", {
              position: "top-center",
              autoClose: 2000,
            });

            // Navigate based on user type
            setTimeout(() => {
              if (userData.usertype === 'Student') {
                navigate('/');
              } else if (userData.usertype === 'Teacher') {
                navigate('/');
              } else if (userData.usertype === 'Admin') {
                navigate('/');
              } else {
                navigate('/');
              }
            }, 1000);

            return; // Exit here - session method worked!
          } else {

          }
        } catch (sessionError) {

        }

        // PRIORITY 2: Dynamic User_id detection (works for all users)

        // Simple session check (only use existing backend APIs)
        await new Promise(resolve => setTimeout(resolve, 2000));

        const sessionResponse = await axios.get(
          `${(process.env.REACT_APP_API_BASE_URL || process.env.REACT_APP_LOCAL_SERVER_API)}/`,
          { withCredentials: true }
        );

        if (sessionResponse.data.valid && sessionResponse.data.User_id) {
          // Session works - use it
          localStorage.setItem('User_id', sessionResponse.data.User_id.toString());
          localStorage.setItem('usertype', response.data.usertype);
          localStorage.setItem('username', formData.username.trim());

          // Handle remember me functionality
          if (rememberMe) {
            localStorage.setItem('rememberMe', 'true');
            localStorage.setItem('rememberedUsername', formData.username.trim());
          } else {
            localStorage.removeItem('rememberMe');
            localStorage.removeItem('rememberedUsername');
          }

          setUserRole(response.data.usertype);

          toast.success("Login successful!", {
            position: "top-center",
            autoClose: 2000,
          });

          setTimeout(() => {
            if (response.data.usertype === 'Student') {
              navigate('/');
            } else if (response.data.usertype === 'Teacher') {
              navigate('/');
            } else if (response.data.usertype === 'Admin') {
              navigate('/');
            } else {
              navigate('/');
            }
          }, 1000);
        } else {
          // Session failed - show error
          toast.error("Login failed: Session could not be created. Please contact support.", {
            position: "top-center",
            autoClose: 5000,
          });
        }

        // Clear form data after successful detection
        setFormData({
          username: "",
          password: "",
        });
        setErrors({});

        // Note: Success message and navigation are handled in findUserIdByUsername function
      } else {
        toast.error("Incorrect username or password! Please try again.", {
          position: toast.POSITION.TOP_CENTER,
          autoClose: 3000,
        });
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error(error.message || "Login failed! Please try again.", {
        position: toast.POSITION.TOP_CENTER,
        autoClose: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className={styles.authPage}>
        <GuestNav />
        {/* <div className={styles.spacer}></div> */}
        <div className={styles.authContainer}>
          {/* ✅ Cross Icon on top-right */}
          <Link to="/" className={styles.closeIcon}>
            <FaTimes />
          </Link>

          <img
            src={login1}
            alt="Login decoration 1"
            className={`${styles.loginImage} ${styles.loginImage1}`}
          />
          <img
            src={login2}
            alt="Login decoration 2"
            className={`${styles.loginImage} ${styles.loginImage2}`}
          />
          <div className={styles.authFormWrapper}>
            <div className={styles.headerContainer}>
              <img
                src={pencil}
                alt="Pencil icon"
                className={styles.pencilIcon}
              />
              <h1 className={styles.authTitle}>Log In to Polymath Kids</h1>
            </div>
            <form className={styles.authForm} onSubmit={handleSubmit}>
              <div className={styles.formGroupEmailField}>
                <div className={styles.inputWrapper}>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    placeholder="Username"
                    value={formData.username}
                    onChange={handleChange}
                    className={errors.username ? `${styles.error}` : ""}
                    disabled={loading}
                    maxLength={100}
                    autoComplete="username"
                  />
                  <FaUser className={styles.inputIcon} />
                </div>
                {errors.username && (
                  <span className={styles.errorMessage}>
                    <FaExclamationCircle /> {errors.username}
                  </span>
                )}
              </div>

              <div className={styles.formGroupPasswordField}>
                <div className={styles.inputWrapper} style={{ height: "100%" }}>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    name="password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleChange}
                    className={errors.password ? `${styles.error}` : ""}
                    disabled={loading}
                    maxLength={50}
                    autoComplete="current-password"
                    style={{ margin: "0" }}
                  />
                  <FaLock className={styles.inputIcon} />
                  <button
                    type="button"
                    className={styles.passwordToggle}
                    onClick={togglePasswordVisibility}
                    disabled={loading}
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                {errors.password && (
                  <span className={styles.errorMessage}>
                    <FaExclamationCircle /> {errors.password}
                  </span>
                )}
              </div>

              <div className={styles.rememberMeContainer}>
                <label className={styles.rememberMeLabel}>
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    className={styles.rememberMeCheckbox}
                  />
                  <span className={styles.rememberMeText}>Remember Me</span>
                </label>
              </div>

              <button
                type="submit"
                className={styles.authButton}
                disabled={loading}
              >
                {loading ? "Logging In..." : "Login"}
              </button>

              <div className={styles.authLinks}>
                <p className={styles.registerLink}>
                  Don't have an account?{" "}
                  <Link to="/signup" className={styles.registerText}>
                    Register
                  </Link>
                </p>
                <Link
                  to="/forgot-password"
                  className={styles.forgotPasswordLink}
                >
                  Forgot Password?
                </Link>
              </div>
            </form>
          </div>
        </div>
      </div>
      <Footer />
      <ToastContainer />
    </>
  );
};

export default SignIn;